/**
 * src/pages/Pricing.jsx
 * Tabla de precios pública — accesible sin login.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { crearCheckout } from '../api/billing'

const PLANES = [
  {
    id: 'basic', nombre: 'Basic', precio: '2,8 UF',
    desc: 'Para empresas que empiezan a monitorizar su CM.',
    features: [
      '1 empresa propia',
      'Hasta 5 competidores',
      'Hasta 3 familias CM',
      'Scraping precios cada 48h',
      'OCs mensual',
      'Historial 180 días',
    ],
    featured: false,
  },
  {
    id: 'pro', nombre: 'Pro', precio: '5,5 UF',
    desc: 'Para equipos comerciales con seguimiento activo.',
    features: [
      'Hasta 3 empresas propias',
      'Hasta 15 competidores',
      'Hasta 5 familias CM',
      'Scraping precios cada 24h',
      'OCs semanal',
      'Historial 12 meses',
      'Predictor de oportunidades',
      'Alertas por email',
    ],
    featured: true,
  },
  {
    id: 'premium', nombre: 'Premium', precio: '11 UF',
    desc: 'Para operaciones con múltiples familias CM.',
    features: [
      'Hasta 6 empresas propias',
      'Competidores ilimitados',
      'Familias CM ilimitadas',
      'Scraping precios cada 24h',
      'OCs diario',
      'Historial completo',
      'Predictor de oportunidades',
      'Alertas en tiempo real',
      'Soporte prioritario',
    ],
    featured: false,
  },
]

export default function Pricing() {
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore(state => state.isAuthenticated)
  const [loading, setLoading] = useState(null)
  const [error, setError] = useState('')

  async function handlePlan(tier) {
    setError('')
    if (!isAuthenticated) {
      navigate(`/register?tier=${tier}`)
      return
    }
    setLoading(tier)
    try {
      const { url } = await crearCheckout(tier)
      window.location.href = url
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al iniciar el pago')
    } finally { setLoading(null) }
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)', padding: '60px 24px' }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '52px' }}>
          <div style={{ fontSize: '11px', fontFamily: 'var(--font-mono)', color: 'var(--color-accent)', letterSpacing: '0.1em', marginBottom: '12px' }}>PRECIOS</div>
          <h1 style={{ fontSize: '36px', fontWeight: 700, letterSpacing: '-0.02em', marginBottom: '12px' }}>Simple y transparente</h1>
          <p style={{ color: 'var(--color-text-2)', fontSize: '16px' }}>14 días de prueba gratis · Sin tarjeta requerida</p>
        </div>

        {error && <div style={{ textAlign: 'center', color: 'var(--color-error)', fontFamily: 'var(--font-mono)', fontSize: '13px', marginBottom: '20px' }}>{error}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '20px' }}>
          {PLANES.map(plan => (
            <div key={plan.id} style={{
              background: 'var(--color-surface)',
              border: `1px solid ${plan.featured ? 'var(--color-accent)' : 'var(--color-border)'}`,
              borderRadius: '14px', padding: '28px',
              display: 'flex', flexDirection: 'column',
              position: 'relative',
            }}>
              {plan.featured && (
                <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: 'var(--color-accent)', color: '#fff', fontSize: '11px', fontWeight: 600, padding: '3px 14px', borderRadius: '999px', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                  Más popular
                </div>
              )}
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--color-text-3)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>{plan.nombre}</div>
              <div style={{ fontSize: '34px', fontWeight: 700, fontFamily: 'var(--font-mono)', letterSpacing: '-0.03em', marginBottom: '4px' }}>
                {plan.precio}<span style={{ fontSize: '15px', color: 'var(--color-text-3)', fontWeight: 400 }}>/mes</span>
              </div>
              <p style={{ fontSize: '13px', color: 'var(--color-text-2)', margin: '8px 0 18px' }}>{plan.desc}</p>
              <hr style={{ border: 'none', borderTop: '1px solid var(--color-border)', margin: '0 0 18px' }} />
              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, marginBottom: '22px' }}>
                {plan.features.map(f => (
                  <li key={f} style={{ fontSize: '13px', color: 'var(--color-text-2)', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <span style={{ color: 'var(--color-accent)', fontSize: '12px', flexShrink: 0, marginTop: '2px' }}>✓</span>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handlePlan(plan.id)}
                disabled={loading === plan.id}
                style={{
                  width: '100%', padding: '11px', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', border: 'none', fontFamily: 'var(--font-ui)',
                  background: plan.featured ? 'var(--color-accent)' : 'var(--color-surface-2)',
                  color: plan.featured ? '#fff' : 'var(--color-text)',
                  border: plan.featured ? 'none' : '1px solid var(--color-border)',
                  opacity: loading === plan.id ? 0.7 : 1,
                }}
              >
                {loading === plan.id ? 'Redirigiendo...' : 'Empezar prueba gratis'}
              </button>
            </div>
          ))}
        </div>

        <p style={{ textAlign: 'center', marginTop: '28px', fontSize: '13px', color: 'var(--color-text-3)' }}>
          Precios en UF · IVA no incluido · Flow convierte a CLP al valor del día · Cancela en cualquier momento
        </p>
      </div>
    </div>
  )
}
